import { useEffect, useRef, useState } from 'react'
import { Form, Input, Select, Switch, type FormInstance } from 'antd'
import { adminApi } from '../api'
import {
  KNOWLEDGE_SCOPE_OPTIONS,
  type KnowledgeScope,
} from '../constants/knowledgeScope'
import {
  KnowledgeDimensionBrowseField,
  KnowledgeFolderMultiBrowseField,
  type FolderDimensionOption,
} from './ui'
import './DatasetRuleFields.css'

export type DatasetFilterFormValues = {
  name?: string
  description?: string
  scope?: KnowledgeScope
  dimension_id?: number
  folder_ids?: number[]
  min_quality?: string
  is_public?: boolean
  status?: 'draft' | 'published'
}

export function buildDatasetFilterJson(v: DatasetFilterFormValues) {
  return {
    include_descendants: true,
    dimension_id: v.dimension_id ?? null,
    folder_ids: v.folder_ids || [],
    categories: [],
    tags_any: [],
    min_quality: v.min_quality || null,
  }
}

export function filterJsonToFormValues(
  filter: Record<string, unknown> | null | undefined,
): Partial<DatasetFilterFormValues> {
  const f = filter || {}
  return {
    dimension_id: f.dimension_id != null ? Number(f.dimension_id) : undefined,
    folder_ids: Array.isArray(f.folder_ids) ? f.folder_ids.map(Number).filter((id) => id > 0) : [],
    min_quality: (f.min_quality as string) || undefined,
  }
}

export const DATASET_MIN_QUALITY_LABEL: Record<string, string> = {
  unknown: '未评及以上',
  low: '低质量及以上',
  medium: '中质量及以上',
  high: '高质量及以上',
}

const QUALITY_OPTIONS = [
  { value: 'unknown', label: DATASET_MIN_QUALITY_LABEL.unknown },
  { value: 'low', label: DATASET_MIN_QUALITY_LABEL.low },
  { value: 'medium', label: DATASET_MIN_QUALITY_LABEL.medium },
  { value: 'high', label: DATASET_MIN_QUALITY_LABEL.high },
]

type Props = {
  form: FormInstance
  showMetaFields?: boolean
  showPublicSwitch?: boolean
  showStatus?: boolean
}

export function DatasetRuleFields({
  form,
  showMetaFields = true,
  showPublicSwitch = false,
  showStatus = false,
}: Props) {
  const [dimensions, setDimensions] = useState<FolderDimensionOption[]>([])
  const [dimensionsLoading, setDimensionsLoading] = useState(false)
  const scope = Form.useWatch('scope', form) as KnowledgeScope | undefined
  const dimensionId = Form.useWatch('dimension_id', form) as number | undefined
  const prevDimensionIdRef = useRef<number | undefined>(undefined)
  const prevScopeRef = useRef<KnowledgeScope | undefined>(undefined)

  useEffect(() => {
    if (!scope) {
      setDimensions([])
      return
    }
    setDimensionsLoading(true)
    adminApi
      .get<{ items: FolderDimensionOption[] }>('/admin/knowledge/folder-dimensions', {
        params: { scope },
      })
      .then((r) => setDimensions(r.data.items || []))
      .catch(() => setDimensions([]))
      .finally(() => setDimensionsLoading(false))
  }, [scope])

  useEffect(() => {
    if (prevScopeRef.current === undefined) {
      prevScopeRef.current = scope
      return
    }
    if (prevScopeRef.current !== scope) {
      form.setFieldsValue({ dimension_id: undefined, folder_ids: [] })
      prevScopeRef.current = scope
      prevDimensionIdRef.current = undefined
    }
  }, [scope, form])

  useEffect(() => {
    if (prevDimensionIdRef.current === undefined) {
      prevDimensionIdRef.current = dimensionId
      return
    }
    if (prevDimensionIdRef.current !== dimensionId) {
      form.setFieldsValue({ folder_ids: [] })
      prevDimensionIdRef.current = dimensionId
    }
  }, [dimensionId, form])

  return (
    <div className="dataset-rule-fields">
      {showMetaFields ? (
        <>
          <Form.Item name="name" label="名称" rules={[{ required: true, message: '请输入名称' }]}>
            <Input maxLength={128} placeholder="如：HR 制度库" />
          </Form.Item>
          <Form.Item name="description" label="说明">
            <Input maxLength={512} placeholder="用途、覆盖范围等" />
          </Form.Item>
        </>
      ) : null}
      <Form.Item
        name="scope"
        label="知识库"
        rules={[{ required: true, message: '请选择知识库' }]}
      >
        <Select options={KNOWLEDGE_SCOPE_OPTIONS} placeholder="请选择" />
      </Form.Item>
      {scope ? (
        <Form.Item
          name="dimension_id"
          label="虚拟分类"
          rules={[{ required: true, message: '请选择虚拟分类' }]}
        >
          <KnowledgeDimensionBrowseField
            block
            variant="input"
            dimensions={dimensions}
            placeholder="请选择"
            disabled={dimensionsLoading}
          />
        </Form.Item>
      ) : null}
      {scope && dimensionId ? (
        <Form.Item
          name="folder_ids"
          label="文件夹"
          rules={[{ required: true, message: '请至少选择一个文件夹' }]}
        >
          <KnowledgeFolderMultiBrowseField
            block
            variant="input"
            scope={scope}
            dimensions={dimensions}
            dimensionIds={[dimensionId]}
            placeholder="请选择"
          />
        </Form.Item>
      ) : null}
      <Form.Item
        name="min_quality"
        label="最低质量"
        extra="未评及以上包含尚未测评的文档；选低/中/高时不含未评文档"
      >
        <Select allowClear placeholder="不限" options={QUALITY_OPTIONS} />
      </Form.Item>
      {showStatus ? (
        <Form.Item name="status" label="发布状态">
          <Select
            options={[
              { value: 'draft', label: '草稿' },
              { value: 'published', label: '已发布' },
            ]}
          />
        </Form.Item>
      ) : null}
      {showPublicSwitch ? (
        <Form.Item name="is_public" label="前台可选" valuePropName="checked">
          <Switch />
        </Form.Item>
      ) : null}
    </div>
  )
}
